/* iceberg-ui */
import { PieChart } from '@auviknetworks/iceberg-ui';

/* MUI */
import { KeyboardArrowDown, KeyboardArrowUp } from '@mui/icons-material';
import {
    Box,
    IconButton,
    TableCell,
    TableRow,
    Typography
} from '@mui/material';

/* Shared */
import { ApplicationNameLinkCell } from 'application/Shared/Components/ApplicationNameLinkCell';
import { UserBubbles } from 'application/Shared/Components/Avatars';
import { popoverContentsColumns } from 'application/Shared/Components/Recharts/PopoverContents';
import { ManagedTable } from 'application/Shared/Components/Table';

/*  Yarn  */
import i18n from 'i18n';

/* Objects */
import { Application } from 'objects/Application';

/* React */
import React, { Component, useMemo } from 'react';
import { withTranslation } from 'react-i18next';

const ShadowITReports = (props) => {
    const { renderingScheduledReport, reportData, reportSetup } = props;

    if (typeof reportData === 'undefined') {
        return null;
    }

    return (
        <Box width="100%">
            {reportData.shadowITShadowITOverview && (
                <ShadowITOverview
                    {...props}
                    renderingScheduledReport={renderingScheduledReport}
                    reportSetup={reportSetup}
                    reportData={reportData.shadowITShadowITOverview}
                />
            )}
            {reportData.shadowITShadowITOverviewBreakdown && (
                <ShadowITOverviewBreakdown
                    {...props}
                    renderingScheduledReport={renderingScheduledReport}
                    reportSetup={reportSetup}
                    reportData={reportData.shadowITShadowITOverviewBreakdown}
                />
            )}
            {reportData.shadowITShadowITBreakdown && (
                <ShadowITBreakdown
                    {...props}
                    renderingScheduledReport={renderingScheduledReport}
                    reportSetup={reportSetup}
                    reportData={reportData.shadowITShadowITBreakdown}
                />
            )}
        </Box>
    );
};

const ShadowITOverview = (props) => {
    const { renderingScheduledReport, reportData, t } = props;

    const categories = [];
    const subCategories = [];

    Object.entries(reportData.data).forEach(([category, values]) => {
        // TODO: Localize categories and subcategories so that one can use
        // name: t(`Categories.${category}`) and below, name: t(`Categories.${subcat}`)
        categories.push({ name: category, value: values.count });
        Object.entries(values.subCategories).forEach(([subcat, value]) => {
            subCategories.push({
                name: subcat,
                value
            });
        });
    });

    return (
        <Box mt="32px" width="100%">
            <Typography variant="h5">
                {t('Reports.CategoryReport.category_overview')}
            </Typography>
            <PieChart
                data={categories}
                emptyMessage={t('Common.noData')}
                isAnimationActive={!renderingScheduledReport}
                popoverContentsColumns={popoverContentsColumns}
                responsiveContainerHeight={250}
            />
        </Box>
    );
};

const headersShadowITOverviewBreakdown = (t) => [
    {
        align: 'left',
        field: 'ilo_cat',
        renderHeader: <strong>{t('Common.category')}</strong>,
        width: '50%'
    },
    {
        align: 'left',
        field: 'ilo_total_applications',
        renderHeader: <strong>{t('Common.applications')}</strong>,
        width: '35%'
    },
    {
        align: 'right',
        field: 'ilo_percent_applications',
        renderHeader: <strong>{t('Common.percent')}</strong>,
        width: '15%'
    }
];

const ShadowITOverviewBreakdown = (props) => {
    const { reportData, reportWrapper, t } = props;

    const categories = Object.entries(reportData.data).reduce(
        (acc, [category, values]) => {
            if (values.count) {
                acc.push({
                    count: values.count,
                    name: category,
                    percent: values.percent
                });
            }
            return acc;
        },
        []
    );

    const rows = useMemo(() => categories, [categories]);

    const tableHeadersShadowITOverviewBreakdown = useMemo(
        () => headersShadowITOverviewBreakdown(t),
        [t]
    );

    return (
        <Box mt="32px" width="100%">
            {!reportWrapper && (
                <Typography variant="h5">
                    {t('Reports.ProceduralCodes.OverviewBreakdown')}
                </Typography>
            )}
            <ManagedTable
                headers={tableHeadersShadowITOverviewBreakdown}
                manageRows
                renderBody={(row) => (
                    <React.Fragment key={`ilo_${row.name}`}>
                        <TableRow
                            sx={{ backgroundColor: 'table.rowBackground' }}
                        >
                            <TableCell>{row.name}</TableCell>
                            <TableCell align="left">{row.count}</TableCell>
                            <TableCell align="right">
                                {`${row.percent} %`}
                            </TableCell>
                        </TableRow>
                    </React.Fragment>
                )}
                renderBodyKey="name"
                renderFooter={false}
                rows={rows}
                searchEnabled={false}
                sortDirection="ASC"
                sortFieldType="string"
                sortKey="name"
            />
        </Box>
    );
};

const headersShadowITBreakdown = [
    {
        align: 'left',
        field: 'ilo_uncollapse',
        renderHeader: <strong />,
        width: '5%'
    },
    {
        align: 'left',
        field: 'ilo_cat',
        renderHeader: <strong>{i18n.t('Common.category')}</strong>,
        width: '45%'
    },
    {
        align: 'left',
        field: 'ilo_app',
        renderHeader: <strong>{i18n.t('Common.applications')}</strong>,
        width: '35%'
    },
    {
        align: 'right',
        field: 'ilo_users',
        renderHeader: <strong>{i18n.t('Common.users')}</strong>,
        width: '15%'
    }
];

class ShadowITBreakdown extends Component {
    constructor(props) {
        super(props);
        this.state = {
            applicationMap: {},
            categoryReport: []
        };
    }

    componentDidMount() {
        const { reportData } = this.props;
        this.setState({
            applicationMap: reportData.applicationMap,
            categoryReport: reportData.categoryReport
        });
    }

    setCategoryOpen = (row) => {
        const { categoryReport } = this.state;

        const updatedCatReport = categoryReport.map((category) => {
            if (row.name === category.name) {
                return {
                    ...category,
                    open: !category.open
                };
            }
            return { ...category };
        });

        this.setState({ categoryReport: updatedCatReport });
    };

    setSubCategoryOpen = (row, subRow) => {
        const { categoryReport } = this.state;
        const updatedCatReport = categoryReport.map((category) => {
            if (row.name === category.name) {
                const updatedSubcatReport = category.subCategories.map(
                    (subcat) => {
                        if (subRow.name === subcat.name) {
                            return {
                                ...subcat,
                                open: !subcat.open
                            };
                        }
                        return { ...subcat };
                    }
                );

                return {
                    ...category,
                    subCategories: updatedSubcatReport
                };
            }
            return { ...category };
        });

        this.setState({ categoryReport: updatedCatReport });
    };

    renderRow = (row) => {
        const { renderingScheduledReport, t } = this.props;
        const { applicationMap } = this.state;

        return (
            <React.Fragment key={`ilo_${row.name}`}>
                <TableRow sx={{ backgroundColor: 'table.rowBackground' }}>
                    <TableCell>
                        {!renderingScheduledReport && (
                            <IconButton
                                aria-label="expand row"
                                size="small"
                                onClick={() => {
                                    this.setCategoryOpen(row);
                                }}
                                sx={{ color: 'icon.textColor' }}
                            >
                                {row.open ? (
                                    <KeyboardArrowUp />
                                ) : (
                                    <KeyboardArrowDown />
                                )}
                            </IconButton>
                        )}
                    </TableCell>
                    <TableCell>
                        <strong>{row.name}</strong>
                    </TableCell>
                    <TableCell>
                        <strong>{row.count}</strong>
                    </TableCell>
                    <TableCell align="right">
                        <strong>{row.userCount}</strong>
                    </TableCell>
                </TableRow>

                {(row.open || renderingScheduledReport) &&
                    row.subCategories.map((data) => (
                        <React.Fragment key={`ilo_subcategory_${data.name}`}>
                            <TableRow
                                sx={{
                                    backgroundColor: 'table.subtableHeader'
                                }}
                            >
                                <TableCell>&nbsp;</TableCell>
                                <TableCell>
                                    {!renderingScheduledReport && (
                                        <IconButton
                                            aria-label="expand row"
                                            size="small"
                                            onClick={() => {
                                                this.setSubCategoryOpen(
                                                    row,
                                                    data
                                                );
                                            }}
                                            sx={{
                                                color: 'icon.textColor'
                                            }}
                                        >
                                            {data.open ? (
                                                <KeyboardArrowUp />
                                            ) : (
                                                <KeyboardArrowDown />
                                            )}
                                        </IconButton>
                                    )}
                                    {renderingScheduledReport && <> - </>}
                                    {data.name}
                                </TableCell>
                                <TableCell align="left">{data.count}</TableCell>
                                <TableCell align="right">
                                    {data.userCount}
                                </TableCell>
                            </TableRow>
                            {(data.open || renderingScheduledReport) &&
                                data.applications.map((applicationId) => (
                                    <TableRow
                                        key={`ilo_application_${applicationId}`}
                                        sx={{
                                            backgroundColor:
                                                'table.subtableBackground'
                                        }}
                                    >
                                        <TableCell>&nbsp;</TableCell>
                                        <TableCell align="left" colSpan={2}>
                                            <Application
                                                data={
                                                    applicationMap[
                                                        applicationId
                                                    ].application
                                                }
                                                fromPage={t(
                                                    'Reports.Title.shadowIT.report'
                                                )}
                                                nameHasLink
                                            >
                                                <ApplicationNameLinkCell
                                                    nameNoTruncate
                                                />
                                            </Application>
                                        </TableCell>
                                        <TableCell align="center">
                                            <UserBubbles
                                                align="right"
                                                max={5}
                                                users={
                                                    applicationMap[
                                                        applicationId
                                                    ].users
                                                }
                                            />
                                        </TableCell>
                                    </TableRow>
                                ))}
                        </React.Fragment>
                    ))}
            </React.Fragment>
        );
    };

    render() {
        const { t } = this.props;
        const { categoryReport } = this.state;

        return (
            <Box className="break-auto" mt="32px">
                <Typography variant="h5">
                    {t('Reports.CategoryReport.category_breakdown')}
                </Typography>
                <ManagedTable
                    headers={headersShadowITBreakdown}
                    manageRows
                    renderBody={this.renderRow}
                    renderBodyKey="name"
                    renderFooter={false}
                    rows={categoryReport}
                    rowsPerPage={-1}
                    searchEnabled={false}
                    sortDirection="DESC"
                    sortFieldType="number"
                    sortKey="usage"
                />
            </Box>
        );
    }
}

export default withTranslation()(ShadowITReports);
